import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Tooltip } from "react-tooltip";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
interface WaveDropRatingsProps {
  readonly drop: ApiDrop;
  readonly winningThreshold?: number | null | undefined;
}
const getFallbackAvatarLabel = (label: string) => {
  const withoutHexPrefix = label.replace(/^0x/i, "");
  const source = withoutHexPrefix || label;
  return source.slice(0, 2).toUpperCase();
};
const WaveDropRatings: React.FC<WaveDropRatingsProps> = t0 => {
  const $ = _c(43);
  const {
    drop,
    winningThreshold
  } = t0;
  const [activeRaterId, setActiveRaterId] = React.useState(null);
  const totalVote = drop.rating;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const hasUserVoted = userVote !== 0;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalVoteClass = totalVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const userVoteClass = userVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const hasWinningThreshold = typeof winningThreshold === "number" && winningThreshold > 0;
  const t1 = `tw-font-medium ${totalVoteClass}`;
  let t2;
  if ($[0] !== totalVote) {
    t2 = formatNumberWithCommas(totalVote);
    $[0] = totalVote;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = hasWinningThreshold ? drop.realtime_rating : drop.rating_prediction;
  const t5 = hasWinningThreshold ? "Realtime votes given" : undefined;
  let t6;
  if ($[5] !== drop.rating || $[6] !== t4 || $[7] !== t5) {
    t6 = <DropVoteProgressing current={drop.rating} projected={t4} tooltipLabel={t5} compact={true} />;
    $[5] = drop.rating;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t3}{t6}</div>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== votingLabel) {
    t8 = <span className="tw-whitespace-nowrap tw-font-normal tw-text-iron-400">{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
    $[12] = votingLabel;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="tw-flex tw-items-center tw-gap-x-2">{t7}{t8}</div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== activeRaterId || $[18] !== drop.id || $[19] !== drop.top_raters || $[20] !== votingLabel) {
    let t11;
    if ($[22] !== activeRaterId || $[23] !== drop.id || $[24] !== drop.top_raters.length || $[25] !== votingLabel) {
      t11 = (rater, index) => (() => {
        const raterLabel = rater.profile.handle ?? rater.profile.primary_address;
        const raterHref = `/${raterLabel}`;
        const tooltipId = `wave-drop-rater-${drop.id}-${rater.profile.id}`;
        const fallbackAvatarLabel = getFallbackAvatarLabel(raterLabel);
        const raterTooltipLabel = `${raterLabel} • ${formatNumberWithCommas(rater.rating)} ${votingLabel}`;
        const baseZIndex = drop.top_raters.length - index;
        const zIndex = activeRaterId === rater.profile.id ? drop.top_raters.length + 1 : baseZIndex;
        return <React.Fragment key={rater.profile.id}><Link href={raterHref} onClick={_temp} onMouseEnter={() => setActiveRaterId(rater.profile.id)} onMouseLeave={() => setActiveRaterId(null)} onFocus={() => setActiveRaterId(rater.profile.id)} onBlur={() => setActiveRaterId(null)} aria-label={raterTooltipLabel} title={raterTooltipLabel} data-tooltip-id={tooltipId} className="tw-relative tw-block tw-transition-transform hover:tw-scale-110 focus-visible:tw-rounded-md focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400/70" style={{
            zIndex
          }}>{rater.profile.pfp ? <Image src={getScaledImageUri(rater.profile.pfp, ImageScale.W_AUTO_H_50)} alt={`${raterLabel}'s avatar`} width={20} height={20} className="tw-h-5 tw-w-5 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-black" /> : <span aria-hidden="true" className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-md tw-bg-iron-800 tw-text-[9px] tw-font-semibold tw-uppercase tw-text-iron-300 tw-ring-1 tw-ring-black">{fallbackAvatarLabel}</span>}</Link><Tooltip id={tooltipId} place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{raterLabel} • {formatNumberWithCommas(rater.rating)}{" "}{votingLabel}</Tooltip></React.Fragment>;
      })();
      $[22] = activeRaterId;
      $[23] = drop.id;
      $[24] = drop.top_raters.length;
      $[25] = votingLabel;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    t10 = drop.top_raters.map(t11);
    $[17] = activeRaterId;
    $[18] = drop.id;
    $[19] = drop.top_raters;
    $[20] = votingLabel;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <div className="tw-flex tw-items-center -tw-space-x-1.5">{t10}</div>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== drop) {
    t12 = <ParticipationDropVoteDetailsTrigger drop={drop} />;
    $[29] = drop;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">{t11}{t12}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== hasUserVoted || $[35] !== userVote || $[36] !== userVoteClass || $[37] !== votingLabel) {
    t14 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-whitespace-nowrap"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}</span><span className={`tw-font-medium ${userVoteClass}`}>{userVote < 0 && "-"}{formatNumberWithCommas(Math.abs(userVote))} {votingLabel}</span></span></div>;
    $[34] = hasUserVoted;
    $[35] = userVote;
    $[36] = userVoteClass;
    $[37] = votingLabel;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14 || $[41] !== t9) {
    t15 = <div className="tw-mt-1 tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-text-sm tw-leading-5">{t9}{t13}{t14}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t9;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
};
export default WaveDropRatings;
function _temp(event) {
  return event.stopPropagation();
}